import { setFromToByScrollingMark } from '@utils/scrollingWindowFromTo';

const { localStorage } = window;

const EXPIRES_SEPARATOR = '.';
const EXPIRES_TEXT = 'expires';

const getExpiresKey = key => `${key}${EXPIRES_SEPARATOR}${EXPIRES_TEXT}`;

class LocalStorage {
  _get = (key) => {
    const expiresKey = getExpiresKey(key);
    const value = localStorage.getItem(key);
    const expires = localStorage.getItem(expiresKey);

    if (typeof expires === 'string') {
      const timestamp = expires.split(EXPIRES_SEPARATOR)[1];
      if (!timestamp) {
        this.remove(key);
        return null;
      }
      const timestampNumber = parseFloat(timestamp);
      if (!timestampNumber || timestampNumber < Date.now()) {
        this.remove(key);
        return null;
      }
    }

    try {
      const res = JSON.parse(value);
      setFromToByScrollingMark(res);
      return res;
    } catch (error) {
      return value;
    }
  }

  _batchGet = keys => keys.reduce((store, currentKey) => {
    const cloneStore = store;
    const value = this.get(currentKey);
    if (value !== null && value !== undefined) {
      cloneStore[currentKey] = value;
    }
    return cloneStore;
  }, {});

  /**
   * @example Save for 3s
   * localStore.set({
   *    key: 'test',
   *    value: { test: 'hello world' },
   *    expires: 3000   // ms
   * })
   * @example Save permanently unless the browser cache is cleared
   * localStore.set({
   *    key: 'test',
   *    value: { test: 'hello world' }
   * })
   */
  set = ({ key, value, expires }) => {
    const expiresKey = getExpiresKey(key);

    if (!value) {
      this.remove(key);
    }

    const state = JSON.stringify(value);

    if (typeof expires === 'number' && expires > 0) {
      localStorage.setItem(key, state);
      localStorage.setItem(expiresKey, `${EXPIRES_TEXT}${EXPIRES_SEPARATOR}${Date.now() + expires}`);
    } else {
      localStorage.setItem(key, state);
    }
  }

  get = (key) => {
    const { _batchGet: batchGet, _get: getValue } = this;
    if (Array.isArray(key) && key.length === 0) {
      return {};
    }
    if (Array.isArray(key)) {
      return batchGet(key);
    }
    if (typeof key === 'string') {
      return getValue(key);
    }
    return batchGet(this.keys());
  }

  remove = (key) => {
    const expired = getExpiresKey(key);
    localStorage.removeItem(expired);
    localStorage.removeItem(key);
  }

  keys = () => Object.keys(localStorage) || [];

  clearAll = () => localStorage.clear();
}

export default new LocalStorage();
